import React, { useEffect, useState, Component } from 'react';
import { X, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const MarketplaceModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT THE USER', `Full name: ${get('fullName')}`, `Work email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, `Job title: ${get('jobTitle')}`, `Country: ${get('country')}`, '', 'COMPANY DETAILS', `Company name: ${get('companyName')}`, `Company website: ${get('website')}`, `Business type: ${get('businessType')}`, '', 'CAMPAIGN DETAILS', `Influencer campaigns per month: ${get('campaignsPerMonth')}`, `Estimated monthly budget: ${get('monthlyBudget')}`, `Type of talent looking for: ${get('talentType')}`, '', 'OTHER INFO', `How did you hear about Zano: ${get('howDidYouHear')}`, `Referred by a Zano user?: ${get('referredBy')}`, `Anything else?: ${get('notes')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Marketplace Access Request',
      from_name: 'Zano — Request Marketplace Access form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-654">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-655">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-656">
          <X size={20} data-id="element-657" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-658">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-659">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-660" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-661">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-662">
              Thank you, we received your request. Our team will get back to you
              within 48 hours. If you are approved, you will receive an
              invitation to create an account as a marketplace user and
              subscribe to a free trial on the marketplace plan. Any questions,
              email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zano-pink hover:underline" data-id="element-663">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button variant="primary" className="w-full sm:w-auto justify-center px-8" onClick={onClose} data-id="element-664">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-665">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-666">
              Request Marketplace Access
            </h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed" data-id="element-667">
              Get access to talent agency rosters, represented influencer
              profiles, marketplace benchmarks, average pricing insights, and
              the Zano Dashboard. Complete the form below and our team will
              review your request.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-668">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-669">
                <div data-id="element-670">
                  <label className={labelClass} data-id="element-671">Full name</label>
                  <input name="fullName" type="text" required className={inputClass} data-id="element-672" />
                </div>
                <div data-id="element-673">
                  <label className={labelClass} data-id="element-674">Work email</label>
                  <input name="email" type="email" required className={inputClass} data-id="element-675" />
                </div>
                <div data-id="element-676">
                  <label className={labelClass} data-id="element-677">LinkedIn profile URL</label>
                  <input name="linkedin" type="url" required className={inputClass} data-id="element-678" />
                </div>
                <div data-id="element-679">
                  <label className={labelClass} data-id="element-680">Job title</label>
                  <input name="jobTitle" type="text" required className={inputClass} data-id="element-681" />
                </div>
                <div data-id="element-682">
                  <label className={labelClass} data-id="element-683">Country</label>
                  <input name="country" type="text" required className={inputClass} data-id="element-684" />
                </div>
                <div data-id="element-685">
                  <label className={labelClass} data-id="element-686">Company name</label>
                  <input name="companyName" type="text" required className={inputClass} data-id="element-687" />
                </div>
                <div data-id="element-688">
                  <label className={labelClass} data-id="element-689">Company website</label>
                  <input name="website" type="url" required className={inputClass} data-id="element-690" />
                </div>
                <div data-id="element-691">
                  <label className={labelClass} data-id="element-692">
                    What kind of business are you?
                  </label>
                  <select name="businessType" required className={inputClass} data-id="element-693">
                    <option value="" disabled selected data-id="element-694">
                      Select an option
                    </option>
                    <option data-id="element-695">Brand</option>
                    <option data-id="element-696">Marketing Agency</option>
                    <option data-id="element-697">Talent Agency</option>
                  </select>
                </div>
                <div data-id="element-698">
                  <label className={labelClass} data-id="element-699">
                    How many influencer campaigns do you run each month?
                  </label>
                  <input name="campaignsPerMonth" type="text" required className={inputClass} data-id="element-700" />
                </div>
                <div data-id="element-701">
                  <label className={labelClass} data-id="element-702">
                    Estimated monthly budget for influencer marketing
                  </label>
                  <input name="monthlyBudget" type="text" required className={inputClass} data-id="element-703" />
                </div>
                <div className="sm:col-span-2" data-id="element-704">
                  <label className={labelClass} data-id="element-705">
                    What kind of talent are you looking to work with?
                  </label>
                  <input name="talentType" type="text" placeholder="e.g. influencers, celebrities, public figures, athletes, B2B LinkedIn" required className={inputClass} data-id="element-706" />
                </div>
                <div className="sm:col-span-2" data-id="element-707">
                  <label className={labelClass} data-id="element-708">
                    How did you hear about Zano?
                  </label>
                  <select name="howDidYouHear" required className={inputClass} data-id="element-709">
                    <option value="" disabled selected data-id="element-710">
                      Select an option
                    </option>
                    <option data-id="element-711">LinkedIn</option>
                    <option data-id="element-712">YouTube</option>
                    <option data-id="element-713">Google</option>
                    <option data-id="element-714">Word of mouth</option>
                    <option data-id="element-715">WIIM</option>
                    <option data-id="element-716">ChatGPT</option>
                    <option data-id="element-717">Referral from existing user</option>
                    <option data-id="element-718">Influencer Marketing Hub</option>
                    <option data-id="element-719">Cannes Lions 2026</option>
                    <option data-id="element-720">Product Hunt</option>
                    <option data-id="element-721">Email outreach</option>
                    <option data-id="element-722">The Creator Network</option>
                    <option data-id="element-723">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-724">
                  <label className={labelClass} data-id="element-725">
                    Were you referred by a Zano user? If yes, who?
                  </label>
                  <input name="referredBy" type="text" className={inputClass} data-id="element-726" />
                </div>
                <div className="sm:col-span-2" data-id="element-727">
                  <label className={labelClass} data-id="element-728">
                    Anything else you would like us to know?
                  </label>
                  <textarea name="notes" rows={3} className={inputClass} data-id="element-729" />
                </div>
              </div>

              <div className="flex items-start gap-3 pt-2" data-id="element-730">
                <input type="checkbox" id="marketplace-tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-731" />
                <label htmlFor="marketplace-tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-732">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-733">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-734">
                {submitting ? 'Sending…' : 'Submit request'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};